import { z } from 'zod';
import { CONTRACT_VALUES } from '@/config/contract';

// Base app configuration schema (matching userConfigurableBaseNumaAppPropsSchema from infrastructure)
const baseAppConfigSchema = z
  .object({
    enableJobs: z.boolean().optional(),
    urlPathPrefix: z.string().optional(),
    s3KeyPrefix: z.string().optional(),
    senderEmail: z.string().optional(),
    receiverEmails: z.array(z.string()).optional(),
  })
  .strict();

// Data source configuration schemas
const webCrawlerConfigSchema = z
  .object({
    url: z.string(),
    maxDepth: z.number().optional(),
    maxPages: z.number().optional(),
  })
  .optional();

const sharePointConfigSchema = z
  .object({
    siteUrl: z.string(),
    tenantId: z.string(),
  })
  .optional();

const boxConfigSchema = z
  .object({
    clientId: z.string(),
    clientSecret: z.string(),
  })
  .optional();

const teamsConfigSchema = z
  .object({
    tenantId: z.string(),
  })
  .optional();

const s3ConfigSchema = z
  .object({
    bucketName: z.string(),
    prefix: z.string().optional(),
  })
  .optional();

const oauthProviderSchema = z.object({
  enabled: z.boolean(),
  clientId: z.string().optional(),
  clientSecret: z.string().optional(),
  scopes: z.array(z.string()),
});

// Complete Client Configuration Schema (matching infrastructure schema in
// infra/stacks/numa-client-stack.ts and infra/constructs/core-numa-infra-construct.ts).
// Keep in sync — JSON upload validates with .strict() and rejects unknown fields.
export const clientConfigSchema = z.object({
  // Core required fields
  clientAccountId: z.string(),
  region: z.string(),

  // Infrastructure settings
  enableIFrame: z.boolean().optional(), // default: false
  indexType: z.enum(['ENTERPRISE', 'STARTER']).optional(),
  indexUnits: z.number().optional(),
  loadSampleFile: z.boolean().optional(),
  createServiceLinkedRole: z.boolean().optional(),
  temporaryPasswordValidityDays: z.number().optional(),
  passwordLength: z.number().optional(),
  mfa: z.boolean().optional(),

  // Development settings
  devInstance: z.boolean().optional(), // default: false
  customDomain: z.string().optional(),
  emailDomain: z.string().optional(),

  // Q Business settings
  qBusinessRegion: z.string().optional(), // default: 'us-east-1'
  provisionQResources: z.boolean().optional(), // default: false
  enableDirectLLMAccess: z.boolean().optional(), // default: true — Q "allow end users to send queries directly to the LLM"
  enableLLMKnowledgeFallback: z.boolean().optional(), // default: true — Q "allow fall back to LLM knowledge"

  // App deployment settings
  allApps: z.boolean().optional(), // default: false
  allProdApps: z.boolean().optional(), // default: false
  apps: z.record(baseAppConfigSchema).optional(), // default: {}
  jobHistory: z.boolean().optional(), // default: true (when allApps is enabled)

  // Knowledge base settings
  preferredKnowledgeBase: z.enum(['q', 'bedrock', 'none']).optional(),
  vectorStorageType: z.enum(['rds', 's3vectors']).optional(), // default: 's3vectors'
  embeddingModel: z.string().optional(), // default: 'amazon.titan-embed-text-v2:0'
  bedrockParserModel: z.string().optional(), // default: 'amazon.nova-lite-v1:0'
  visionModelType: z.enum(['haiku', 'nova-pro', 'luna']).optional(), // default: 'luna' (GPT-5.6 Luna on Bedrock, 2026-08-26 bake-off)
  knowledgeBases: z.boolean().optional(), // default: true
  recordsKBEnabled: z.boolean().optional(),
  contentSearchEnabled: z.boolean().optional(),

  // Communication settings
  senderEmail: z.string().optional(),
  receiverEmails: z.array(z.string()).optional(),
  bedrockAccount: z.string().optional(),

  // Feature flags
  numaChatAgents: z.boolean().optional(), // default: true
  allowBedrockQuotaSharing: z.boolean().optional(), // default: false
  pipedreamIntegrations: z.boolean().optional(), // default: false
  dataConnectorsEnabled: z.boolean().optional(), // default: false
  cmsIsscConnector: z.boolean().optional(), // default: false — FEAT-409; gates the CMS connector, which reaches ACC sensitive-claims data. Keep OFF until the practice has passed the handling review (TASK-434). Effective only when dataConnectorsEnabled is also true
  synergy: z.boolean().optional(), // default: false — single flag gating ALL Synergy 12d functionality (Files-page browser + read-parity routes, Synergy Explorer structured data store app); effective only when dataConnectorsEnabled is also true
  synergyFileParity: z.boolean().optional(), // @deprecated IGNORED (KB-crawler cutover); tolerated so old records parse
  synergyKbCrawl: z.boolean().optional(), // @deprecated IGNORED (KB-crawler cutover); tolerated so old records parse
  synergyTermIndex: z.boolean().optional(), // @deprecated IGNORED (KB-crawler cutover); tolerated so old records parse
  agents: z.boolean().optional(), // default: false
  brandingProviderEnabled: z.boolean().optional(), // default: false
  brandingAssetsBucketArn: z.string().optional(),
  brandingAssetsPrefix: z.string().optional(),
  numaWorkspaceChat: z.boolean().optional(), // default: true
  agentCoreRegion: z.string().optional(), // default: client region
  useGlobalInferenceProfile: z.boolean().optional(), // default: true (global Bedrock CRI; false routes to regional us./au./apac.* for tight-SCP customers)
  scheduling: z.boolean().optional(), // default: false
  /**
   * Sub-flag of `scheduling`. When `scheduling: true` and `eventTriggers: false`,
   * cron schedules work but event triggers (Gmail-message etc.) are hidden
   * from the user-facing automation builder, the admin Settings >
   * Scheduling page, and trigger-quota fields here in the CSP.
   *
   * Default `false` — new clients must opt in via the CSP form. Existing
   * clients that had scheduling on at the time this flag landed were
   * backfilled to `true` via `tools/backfill-triggers-flag.ts`. Has no effect
   * when `scheduling: false`.
   */
  eventTriggers: z.boolean().optional(),
  schedulingMinIntervalMinutes: z.number().int().min(5).optional(), // per-client min interval override
  // Per-client quota overrides (Level 2). Unset → fall back to platform-settings (Level 1).
  // 0 is a valid value for caps — means "0 allowed" (a way to disable a quota target).
  maxRunsPerCompanyPerMonth: z.number().int().min(0).optional(),
  maxRunsPerUserPerMonth: z.number().int().min(0).optional(),
  maxTriggerRunsPerCompanyPerMonth: z.number().int().min(0).optional(),
  maxTriggerRunsPerUserPerMonth: z.number().int().min(0).optional(),
  maxConcurrentActiveSchedulesPerCompany: z.number().int().min(0).optional(),
  maxConcurrentActiveSchedulesPerUser: z.number().int().min(0).optional(),
  requireApprovalAboveUserCap: z.boolean().optional(),
  // Arcanum's limits on public pages (infra numa-client-stack.ts). Unset → the default.
  publicAiMaxDailyCalls: z.number().int().min(1).max(100_000).optional(),
  publicAiMaxHourlyCredits: z.number().min(0.01).max(1_000).optional(),
  publicAiMaxDailyCredits: z.number().min(0.01).max(10_000).optional(),
  publicAiMaxOutputTokens: z.number().int().min(100).max(8_000).optional(),
  publicFormVisitorHourlyCap: z.number().int().min(1).max(10_000).optional(),
  publicFormVisitorDailyCap: z.number().int().min(1).max(10_000).optional(),
  publicFormLinkHourlyCap: z.number().int().min(1).max(10_000).optional(),
  workspaceChatModelSelection: z.boolean().optional(), // default: true — shows the in-chat model picker (only when >1 model available)
  bedrockModelsOnly: z.boolean().optional(), // default: false — constrains available models to AWS Bedrock only (drops the Lite tier on the relay; Standard is Claude Haiku 5.5 on Bedrock and stays)
  standardModelTransport: z.enum(['bedrock', 'relay']).optional(), // default: 'bedrock' — how Standard reaches GPT-6 Luna when it runs on Luna (the fallback, the NUMA_STANDARD_MODEL=luna rollback); Standard itself is Claude Haiku 5.5 on Bedrock
  lightModelTransport: z.enum(['bedrock', 'relay']).optional(), // default: 'bedrock' (GPT-6 Luna in-account, medium effort); 'relay' = rollback to the deployer relay
  defaultChatModel: z.enum(['light', 'standard', 'premium', 'expert']).optional(), // default model tier; absent → derived from the two flags above
  numaOps: z.boolean().optional(), // default: false
  contacts: z.boolean().optional(), // default: false — Numa Contacts (first-class contact system; no Ops/Voice dependency)
  contactsDefaultPhoneRegion: z.string().optional(), // ISO 3166-1 alpha-2, e.g. 'NZ' — phone parsing region for Contacts
  contactsTimezone: z.string().optional(), // IANA timezone for Contacts date reminders
  // The Contacts telephony tree. Every one is ANDed with its parent when
  // config.json is generated (numa-client-stack `contactsOn` / `telephonyOn`) —
  // the metadata `dependencies:` field is display-only and enforces nothing.
  contactsTelephony: z.boolean().nullish(), // default: false — presence, claimed numbers, pickup groups, softphone. Provisions billable AWS resources
  contactsCallIntelligence: z.boolean().nullish(), // default: false — recording, transcription, post-call analysis. METERED per minute
  contactsCallAnalytics: z.boolean().nullish(), // default: false — the Call Analytics page. Child of call intelligence: nothing transcribed ⇒ nothing to report
  contactsCallLists: z.boolean().nullish(), // default: false — ordered call lists, optionally projected into an Ops zone
  contactsAiCalls: z.boolean().nullish(), // default: false — AI-placed/answered calls. System-managed; never admin-toggleable
  contactsPresence: z.boolean().nullish(), // default: false — workspace-wide Online/Away/Offline on the contact record
  contactsOpsLink: z.boolean().nullish(), // default: false — two-way call-list ↔ Ops board sync. The only Ops-dependent one
  partners: z.boolean().nullish(), // default: false — SPK-004 Partners entity in Numa Ops (fails closed; null → false)
  awsPartnerCentral: z.boolean().nullish(), // default: false — AWS Partner Central in the Ops CRM. Needs numaOps and awsPartnerCentralConfig
  // The connection awsPartnerCentral uses; alone it turns nothing on. roleArn is
  // the role tools/aws-partner-central/linked-account.yaml makes in the linked
  // account. Keep IN SYNC with the infra schema.
  awsPartnerCentralConfig: z
    .object({
      linkedAccountId: z.string().regex(/^\d{12}$/, 'must be a 12-digit AWS account ID'),
      roleArn: z.string().regex(/^arn:aws:iam::\d{12}:role\/[\w+=,.@/-]+$/, 'must be an IAM role ARN'),
      externalId: z
        .string()
        .min(16)
        .max(1224)
        .regex(/^[\w+=,.@:/-]+$/, 'may hold only letters, digits and + = , . @ : / _ -'),
      catalog: z.enum(['Sandbox', 'AWS']).optional(),
      referralStageId: z.string().optional(),
      referralCrmId: z.string().optional(),
    })
    .refine((v) => v.roleArn.split(':')[4] === v.linkedAccountId, {
      message: 'roleArn must be a role in linkedAccountId',
      path: ['roleArn'],
    })
    .optional(),
  numaDropZones: z.boolean().optional(), // default: true — GA since FEAT-477
  numaSharing: z.boolean().optional(), // default: true — GA since FEAT-477
  // @deprecated TASK-146 — vault now follows `dataConnectorsEnabled`. Kept on
  // the schema so legacy DynamoDB items with this field still parse.
  secretsVaultEnabled: z.boolean().optional(),
  // @deprecated FEAT-239 — merged into `devInstance`; value ignored. Kept so
  // records still carrying the key parse until the data cleanup happens.
  developerMode: z.boolean().optional(),
  // @deprecated FEAT-239 — merged into `dataConnectorsEnabled`; value ignored.
  oauthIntegrationsEnabled: z.boolean().optional(),
  // @deprecated — superseded by COMPANY vault secrets; nothing reads it.
  oauthProviders: z.record(z.string(), oauthProviderSchema).optional(),
  ssoEnabled: z.boolean().optional(), // default: true
  ssoEnterprise: z.boolean().optional(), // default: false
  chatSuggestions: z.boolean().optional(), // default: true (capability availability)
  siteWideSearch: z.boolean().optional(), // default: false
  connectorAccessReview: z.boolean().optional(), // deprecated 2026-10-02: no effect, a role decides; kept so stored configs parse (kept in sync with infra clientConfigSchema)
  racetechDataFeed: z.boolean().optional(), // default: false
  disasterRecovery: z.boolean().optional(), // default: false
  vpnEnabled: z.boolean().optional(), // default: false — FEAT-227 Site-to-Site VPN (kept in sync with infra clientConfigSchema)
  // Certificate sign-in: Numa issues each person a certificate for their own
  // machine, and that certificate opens the workspace. For a customer who
  // turned single sign-on down and still wants only their own people, on their
  // own machines, to reach their Numa.
  //
  // This schema is .strict(), so until the key is here the portal REJECTS any
  // config that carries it with unrecognized_keys — the flag could not be set
  // for a customer at all, and a record set by hand would trip the same error
  // on its next save. Kept in sync with the infra clientConfigSchema.
  certificateSignIn: z.boolean().optional(), // default: false
  // Settings > Network > Client certificates. Kept in sync with the infra
  // clientConfigSchema, which is .strict() too.
  connectorClientCertificates: z.boolean().optional(), // default: false
  // Fixed outgoing IP: builds billable AWS resources; see the infra schema.
  fixedEgressIp: z.boolean().optional(), // default: false
  fixedEgressRegion: z.string().optional(),
  // Both keys, because the infra schema has both. What actually turns a
  // password away is the workspace's own certificate settings, which an
  // administrator changes on the settings page; this one only decides which
  // control the sign-in page shows before anybody presses anything. Leaving it
  // out of this file would make the portal reject, on its next save, any config
  // a deploy had written it into.
  certificateSignInRequired: z.boolean().optional(), // default: false

  // Who owns the user pool's invitation / password-reset email. `client-app`
  // means the client's own Terraform owns customMessage, the CustomEmailSender
  // and the sender's KMS key, and the numa stack stops RE-ASSERTING all three
  // (it still writes them when it creates the pool, and still builds the
  // sender — it just puts them under the pool's ignore_changes) — both Nolia
  // frontends attach their own branded Lambda and then clear the sender, so
  // before this every numa deploy put their people back onto Numa-branded mail
  // carrying Numa links (BUG-167). A getnolia.io email domain gets this
  // behaviour with no key set; `numa` is the explicit opt-out.
  //
  // This schema is .strict(), so until the key is here the portal REJECTS any
  // config that carries it with unrecognized_keys — the flag could not be set
  // for a customer at all, and a record set by hand would trip the same error
  // on its next save. WHICH MEANS ORDERING: deploy this portal BEFORE writing
  // the key into any numa-client-config record, or the portal rejects those
  // records on load and on save. Kept in sync with the infra
  // clientConfigSchema.
  cognitoEmailBrandingOwner: z.enum(['numa', 'client-app']).optional(), // default: 'numa'
  // v2-apps-api cross-user widenings from a caller without the backend service
  // token: 'log' allows + logs, 'enforce' denies. Kept in sync with the infra
  // clientConfigSchema (numa-client-stack.ts). Same ORDERING rule as above:
  // deploy this portal before writing the key into a client-config record.
  backendServiceTokenMode: z.enum(['log', 'enforce']).optional(), // default: 'log'

  // Mobile & desktop shells — kept in sync with the infra schema in
  // numa-client-stack.ts. They were missing here, and because this schema is
  // .strict() the portal REJECTED any config carrying them with
  // unrecognized_keys — so FEAT-422's mobile push flag could not be set from
  // the portal at all, and a record set by hand tripped the same error on the
  // next save.
  mobilePushEnabled: z.boolean().optional(), // default: false — FEAT-422 push notifications for the Numa mobile shell
  voiceRecordingDefaultMinutes: z.number().int().min(1).max(240).optional(), // default: 120. BUG-653/FEAT-606/BUG-654 default voice recording limit, in minutes
  expoAccessToken: z.string().optional(), // only needed when the Expo project enables enhanced push security
  numaDesktop: z.boolean().optional(), // default: false — Numa Desktop companion app (dev-only, not customer-ready)

  // Numa Voice (Amazon Connect SDR telephony — FEAT-158/169) — kept in sync
  // with the infra schema in numa-client-stack.ts. connectInstanceUrl,
  // recordingsBucket and didNumbers are written back into the config map at
  // runtime by numa-voice-config-writer; without them here the portal rejects
  // any voice-enabled client config with unrecognized_keys.
  numaVoice: z.boolean().optional(), // default: false
  connectAutoProvision: z.boolean().optional(), // default: false
  connectClaimDid: z.boolean().optional(), // default: false
  connectInstanceUrl: z.string().optional(),
  recordingsBucket: z.string().optional(),
  didNumbers: z.array(z.string()).optional(),
  voiceCallPrepTime: z
    .string()
    .regex(/^([01]?\d|2[0-3]):[0-5]\d$/, "voiceCallPrepTime must be 'HH:MM' 24-hour")
    .optional(), // morning call-list run (default 07:30) — same regex as the infra schema so bad input is rejected here, not at synth
  voiceCallPrepTimezone: z.string().optional(), // IANA tz (default Pacific/Auckland)
  voiceLiveAssist: z.boolean().optional(), // default: false — realtime Contact Lens live assist (billed/min)
  enableOpenApiDocs: z.boolean().optional(), // default: false
  appBuilder: z.boolean().optional(), // default: false — TKT-226 App Builder agent + APP-shaped authoring (form inputs, in-render actions, headless). Dashboards are always on and need no flag.
  internalOnly: z.boolean().optional(), // default: false — Arcanum-internal features (Codebase Expert agent). Never enable on a customer instance.
  productDemoAgent: z.boolean().optional(), // default: false — FEAT-617 Product Demo agent under Chat (screen recording → demo video + PDF guide). Customer-ready; each person voices with their own HeyGen connection, so it needs Pipedream integrations.
  homeDashboard: z.boolean().optional(), // default false everywhere — opt-in. Moves the post-login landing page to /home.
  noliaSmokeTest: z.boolean().optional(), // default: false — FEAT-080 Nolia smoke mode (one Haiku session per step). Dev stacks only.
  // @deprecated FEAT-575 — Import from Claude has no per-client switch and
  // always allows full chats; both values are IGNORED. Kept on the schema so
  // records still carrying the keys parse under .strict().
  claudeImport: z.boolean().optional(),
  claudeImportFullChats: z.boolean().optional(),
  voiceMode: z.boolean().optional(), // default false — FEAT-468 spoken conversation with Numa (Nova 2 Sonic; US, Stockholm and Tokyo regions only).
  communityBaseUrl: z.string().optional(), // FEAT-469: override the central community service URL (dev/staging of the community itself).
  managedReleaseSigningKeyArn: z.string().optional(), // Managed apps: the KMS key this workspace's Community proxy signs approved app updates with. Arcanum's workspace (and a managed-apps dev stack) only.
  managedReleaseTrustedKeys: z.array(z.object({ keyId: z.string(), pem: z.string() })).optional(), // Managed apps: extra signing keys this workspace trusts. Dev stacks only.
  homeChatSignals: z.boolean().optional(), // default false — follow-up chat rows in Home's Needs attention.
  claudeCodeSetup: z.boolean().optional(), // default: false — SPK-036 "Connect Claude" setup page: the guided install for the numa CLI + Numa plugin for Claude Code. Frontend-only. Internal testing only.
  // @deprecated TKT-226 — dashboards are always on; the value is IGNORED. Kept
  // on the schema so records still carrying the key parse under .strict()
  // until they are swept via the Config Hygiene panel / Bulk cleanup.
  workflowsAsApps: z.boolean().optional(),
  // @deprecated TASK-550 — V2 apps are app picks like any other; the value is
  // IGNORED. Kept on the schema so records still carrying the key parse under
  // .strict() until they are swept via the Config Hygiene panel / Bulk cleanup.
  v2Apps: z.boolean().optional(),
  // @deprecated FEAT-469 — the Numa Community has no per-client switch; the
  // value is IGNORED. Staff approve a workspace in the Community console. Kept
  // on the schema so records still carrying the key parse under .strict().
  numaCommunity: z.boolean().optional(),
  publicDemo: z.boolean().optional(), // default: false
  publicDemoDailyLimitUsd: z.number().optional(), // default: 10

  // Numa Files
  numaFiles: z.boolean().optional(),

  // Forward-compat / internal — kept so existing DynamoDB items parse under .strict().
  // transcriptionService mirrors the infra `z.any()` forward-compat field; logGroup is an
  // internal infra-only prop (never persisted) included for full schema parity.
  transcriptionService: z.any().optional(),
  logGroup: z.any().optional(),

  // Whitelabel / multi-frontend
  additionalCognitoClientIds: z.string().optional(),
  additionalOrigins: z.array(z.string()).optional(),

  // Cognito group → feature mapping
  groups: z.record(z.string(), z.array(z.string())).optional(),

  // Data source configurations
  webCrawlerConfigs: z.array(webCrawlerConfigSchema).optional(),
  sharePointConfigs: z.array(sharePointConfigSchema).optional(),
  boxConfigs: z.array(boxConfigSchema).optional(),
  teamsConfigs: z.array(teamsConfigSchema).optional(),
  s3Configs: z.array(s3ConfigSchema).optional(),

  // Budget configuration
  budget: z
    .object({
      name: z.string(),
      limitAmount: z.number(),
      alertThresholds: z.array(z.number()),
    })
    .optional(),

  // Numa Credit System (SPK-015). showCredits gates the in-app admin view; creditConfig is the
  // central pricing config authored here and pushed to the client (kept in sync with infra schema).
  showCredits: z.boolean().optional(),
  contractType: z.enum(['credits', 'legacy', 'own-account']).optional(), // default: 'legacy' — how the client is billed; the portal writes it from the client's contract (src/config/contract.ts). 'credits' and 'own-account' turn on the default-model settings (FEAT-604); 'own-account' = the client pays AWS directly, so its people see real AWS dollars and no credits
  usageReporting: z.boolean().optional(), // deprecated 2026-10-02: no effect, a role decides; kept so stored configs parse
  creditConfig: z
    .object({
      creditUsd: z.number().positive().optional(),
      margin: z.number().min(1).optional(),
      agentcoreMult: z.number().min(1).optional(),
      trivialConsumptionUsd: z.number().min(0).optional(),
      // Fixed outgoing IP: charged at AWS cost x this (default 1.05, cost + 5%).
      fixedEgressMargin: z.number().min(1).optional(),
      valueTiers: z.record(z.string(), z.record(z.string(), z.number())).optional(),
      marginsByTier: z.record(z.string(), z.number()).optional(),
      monthlyAllocations: z.array(z.number().min(0)).optional(),
      // Numa Voice consumption rates (USD/min): telephony + transcribe (+ contact lens).
      // The non-LLM cost of a call; metered into the same ledger via numa-voice-credit-debit.
      voiceRates: z
        .object({
          telephonyPerMin: z.number().min(0).optional(),
          transcribePerMin: z.number().min(0).optional(),
          contactLensPerMin: z.number().min(0).optional(),
        })
        .optional(),
      // @deprecated synergyRates (crawler-era estimate knobs) — RETIRED.
      // Synergy store syncs bill at MEASURED cost since TASK-380 (run LLM
      // spend + self-reported lambda GB-s, ×1.25; synergy-store-sync/credits.py)
      // with no per-client rate card. Tolerated only so old records parse;
      // the portal no longer seeds, merges, or edits it.
      synergyRates: z
        .object({
          embedUsdPerMtoken: z.number().min(0).optional(),
          charsPerToken: z.number().min(0).optional(),
          avgTokensPerDoc: z.number().min(0).optional(),
          overheadMult: z.number().min(0).optional(),
        })
        .optional(),
    })
    .optional(),
});

export type ClientConfig = z.infer<typeof clientConfigSchema>;
export type CreditConfig = NonNullable<ClientConfig['creditConfig']>;

// Client metadata (non-deployment) — stored in a separate table from client config.
// The contract and partner live here: see src/config/contract.ts for why.

// Which AWS Organization the client's account belongs to.
//   - nextgen: modern Arcanum-owned per-client accounts under the NextGen org (mgmt 282304106064)
//   - arcanum: Arcanum's internal/dev/HQ/quota-sharing org
//   - standalone: customer-owned account, not in either Arcanum-controlled org
export const ACCOUNT_ORG_VALUES = ['nextgen', 'arcanum', 'standalone'] as const;
export type AccountOrgValue = (typeof ACCOUNT_ORG_VALUES)[number];

// Two tags a person puts on a client by hand. Both are portal-only: neither
// is deployed, and neither changes anything in the client's workspace. A tag
// is on while its attribute is on the row. Lifting a tag removes the attribute.
//   - deployBlock: do not deploy to this client. A single deploy shows the
//     reason and asks for the client's name; a group deploy skips the client.
//   - inactive: a stack nobody uses (a trial that went nowhere). It is left
//     out of the client pickers, the automatic group and the counts by default.
export const deployBlockSchema = z.object({
  reason: z.string().trim().min(1),
  setBy: z.string(),
  setAt: z.string(),
});
export type DeployBlock = z.infer<typeof deployBlockSchema>;

export const inactiveTagSchema = z.object({
  reason: z.string().optional(),
  setBy: z.string(),
  setAt: z.string(),
});
export type InactiveTag = z.infer<typeof inactiveTagSchema>;

export const clientMetadataSchema = z.object({
  clientName: z.string(),
  // Optional: a row can hold only the client's organisation, or only a tag.
  // The contract (all six values) and the partner a partner client came
  // through. The config's contractType holds the billing the contract saves.
  contract: z.enum(CONTRACT_VALUES).optional(),
  partner: z.string().optional(),
  trialStartDate: z.string().optional(),
  trialEndDate: z.string().optional(),
  notes: z.string().optional(),
  accountOrg: z.enum(ACCOUNT_ORG_VALUES).optional(),
  deployBlock: deployBlockSchema.optional(),
  inactive: inactiveTagSchema.optional(),
  updatedAt: z.string().optional(),
  updatedBy: z.string().optional(),
});

export type ClientMetadata = z.infer<typeof clientMetadataSchema>;

export const ACCOUNT_ORG_DISPLAY: Record<AccountOrgValue, { label: string; variant: string }> = {
  nextgen: { label: 'NextGen', variant: 'dark' },
  arcanum: { label: 'Arcanum', variant: 'info' },
  standalone: { label: 'Standalone', variant: 'secondary' },
};

export function getAccountOrgBadgeInfo(org?: AccountOrgValue | null): { label: string; variant: string } | null {
  return org ? ACCOUNT_ORG_DISPLAY[org] : null;
}

// Helper to get default values for display
export const getDefaultClientConfigValues = () => ({
  enableIFrame: false,
  devInstance: false,
  provisionQResources: false,
  allApps: false,
  allProdApps: false,
  apps: {},
  qBusinessRegion: 'us-east-1',
  preferredKnowledgeBase: 'bedrock' as const,
  vectorStorageType: 's3vectors' as const,
  embeddingModel: 'amazon.titan-embed-text-v2:0',
  bedrockParserModel: 'amazon.nova-lite-v1:0',
  visionModelType: 'luna' as const,
  numaChatAgents: true,
  allowBedrockQuotaSharing: false,
  pipedreamIntegrations: false,
  dataConnectorsEnabled: false,
  // Master switch for ALL Synergy 12d functionality (Files-page browser +
  // read-parity routes, Synergy Explorer structured data store app). Effective
  // only when dataConnectorsEnabled is also true. Replaces the deprecated
  // synergyFileParity/synergyKbCrawl/synergyTermIndex flags.
  synergy: false,
  agents: false,
  brandingProviderEnabled: false,
  numaWorkspaceChat: true,
  useGlobalInferenceProfile: true,
  scheduling: false,
  // Defaults to false. Existing clients with scheduling already on were
  // backfilled by tools/backfill-triggers-flag.ts so they keep triggers;
  // any new client must explicitly opt in via the Agent Automations form.
  eventTriggers: false,
  workspaceChatModelSelection: true,
  bedrockModelsOnly: false,
  standardModelTransport: 'bedrock' as const,
  lightModelTransport: 'bedrock' as const,
  numaOps: false,
  contacts: false,
  numaDropZones: true,
  numaSharing: true,
  // Numa Credit System (SPK-015). Gates the in-app Credits dashboard + in-chat
  // credit indicator; metering runs regardless. Pricing is authored on the
  // dedicated Credits page. On by default since the credits GA rollout.
  showCredits: true,
  oauthIntegrationsEnabled: false,
  ssoEnabled: true,
  ssoEnterprise: false,
  appBuilder: false,
  internalOnly: false,
  productDemoAgent: false,
  homeDashboard: false,
  noliaSmokeTest: false,
  voiceMode: false,
  homeChatSignals: false,
  publicDemo: false,
  publicDemoDailyLimitUsd: 10,
  mfa: false,
  numaVoice: false,
  contactsTelephony: false,
  contactsCallIntelligence: false,
  contactsCallAnalytics: false,
  contactsCallLists: false,
  contactsAiCalls: false,
  contactsPresence: false,
  contactsOpsLink: false,
  connectAutoProvision: false,
  connectClaimDid: false,
  voiceLiveAssist: false,
  vpnEnabled: false,
  certificateSignIn: false,
  certificateSignInRequired: false,
  connectorClientCertificates: false,
  fixedEgressIp: false,
  cognitoEmailBrandingOwner: 'numa',
  backendServiceTokenMode: 'log' as const,
  claudeCodeSetup: false,
  mobilePushEnabled: false,
  numaDesktop: false,
});

// Helper to check if a config value differs from default
export const isCustomValue = (key: keyof ClientConfig, value: any): boolean => {
  const defaults = getDefaultClientConfigValues();
  const defaultValue = defaults[key as keyof typeof defaults];

  // Special handling for different value types
  if (defaultValue === undefined) {
    return value !== undefined && value !== null && value !== '';
  }

  return value !== defaultValue;
};

// Helper to get human-readable field names
export const getFieldDisplayName = (key: keyof ClientConfig): string => {
  const fieldNames: Record<string, string> = {
    clientAccountId: 'Account ID',
    region: 'Region',
    devInstance: 'Development Instance',
    allProdApps: 'All Production Apps',
    apps: 'Selected Applications',
    pipedreamIntegrations: 'Pipedream Integrations',
    dataConnectorsEnabled: 'Native Integrations',
    allowBedrockQuotaSharing: 'Bedrock Quota Sharing',
    preferredKnowledgeBase: 'Numa Files backend',
    qBusinessRegion: 'Q Business Region',
    embeddingModel: 'Embedding Model',
    bedrockParserModel: 'Parser Model',
    visionModelType: 'Vision Model Type',
    numaChatAgents: 'Numa Chat Agents',
    agents: 'Agents',
    scheduling: 'Agent Automations',
    eventTriggers: 'Event Triggers',
    schedulingMinIntervalMinutes: 'Minimum Automation Interval (minutes)',
    maxRunsPerCompanyPerMonth: 'Max Schedule Runs / Company / Month',
    maxRunsPerUserPerMonth: 'Max Schedule Runs / User / Month',
    maxTriggerRunsPerCompanyPerMonth: 'Max Trigger Runs / Company / Month',
    maxTriggerRunsPerUserPerMonth: 'Max Trigger Runs / User / Month',
    maxConcurrentActiveSchedulesPerCompany: 'Max Concurrent Active Automations / Company',
    maxConcurrentActiveSchedulesPerUser: 'Max Concurrent Active Automations / User',
    requireApprovalAboveUserCap: 'Require Admin Approval Above User Cap',
    publicAiMaxDailyCalls: 'Public AI Uses / Day',
    publicAiMaxHourlyCredits: 'Public AI Credits / Hour',
    publicAiMaxDailyCredits: 'Public AI Credits / Day',
    publicAiMaxOutputTokens: 'Public AI Longest Answer (tokens)',
    publicFormVisitorHourlyCap: 'Public Form Sends / Visitor / Hour',
    publicFormVisitorDailyCap: 'Public Form Sends / Visitor / Day',
    publicFormLinkHourlyCap: 'Public Form Sends / Link / Hour',
    v2Apps: 'V2 Apps',
    appBuilder: 'App Builder',
    internalOnly: 'Internal Only Features',
    productDemoAgent: 'Product Demo',
    noliaSmokeTest: 'Nolia Smoke Mode',
    homeDashboard: 'Home Page',
    claudeImport: 'Import from Claude',
    claudeImportFullChats: 'Import from Claude: full chats',
    voiceMode: 'Voice Mode',
    numaCommunity: 'Numa Community',
    homeChatSignals: 'Chat signals on Home',
    mfa: 'Multi-Factor Authentication (MFA)',
    numaDropZones: 'Drop Zones',
    numaSharing: 'Sharing',
    ssoEnabled: 'SSO Self-Service',
    ssoEnterprise: 'SSO Enterprise (SCIM/OIDC)',
    developerMode: 'Developer Mode',
    oauthIntegrationsEnabled: 'OAuth Cloud Storage',
    vpnEnabled: 'Site-to-Site VPN',
    certificateSignIn: 'Certificate sign-in',
    certificateSignInRequired: 'Certificate sign-in offered first',
    connectorClientCertificates: 'Connector certificates',
    fixedEgressIp: 'Fixed outgoing IP',
    fixedEgressRegion: 'Fixed outgoing IP region',
    cognitoEmailBrandingOwner: 'Cognito email branding owner',
    backendServiceTokenMode: 'Backend service token mode',
    claudeCodeSetup: 'Connect Claude (Claude Code setup)',
    mobilePushEnabled: 'Mobile Push Notifications',
    voiceRecordingDefaultMinutes: 'Voice Recording Default Limit (minutes)',
    expoAccessToken: 'Expo Access Token',
    numaDesktop: 'Numa Desktop',
    useGlobalInferenceProfile: 'Use Global Bedrock Inference Profile',
    standardModelTransport: 'Standard Model Transport',
    lightModelTransport: 'Lite Model Transport',
  };

  return fieldNames[key] || key;
};

// Helper to identify which fields are considered "advanced"
export const isAdvancedField = (key: keyof ClientConfig): boolean => {
  const advancedFields = [
    'webCrawlerConfigs',
    'sharePointConfigs',
    'boxConfigs',
    'teamsConfigs',
    's3Configs',
    'budget',
    'embeddingModel',
    'bedrockParserModel',
    'visionModelType',
    'customDomain',
    'senderEmail',
    'receiverEmails',
    'bedrockAccount',
    'indexType',
    'indexUnits',
    'loadSampleFile',
    'createServiceLinkedRole',
    'temporaryPasswordValidityDays',
    'passwordLength',
    'mfa',
  ];

  return advancedFields.includes(key);
};

// Client with status information
export interface Client {
  name: string;
  config: ClientConfig;
  status: 'healthy' | 'warning' | 'error' | 'pending';
  lastDeployment?: {
    timestamp: string;
    imageTag: string;
    status: 'success' | 'failed' | 'running';
    deploymentId: string;
  };
  deploymentCount: number;
}

// ECR Image Information
export interface ECRImage {
  repository: string;
  tag: string;
  digest: string;
  pushedAt: string;
  sizeMb: number;
  gitCommit?: string;
  gitBranch?: string;
  customName?: string;
  description?: string;
}

export interface DeploymentGroup {
  groupName: string;
  clients: string[];
  description?: string;
  maxConcurrency?: number;
  managed?: boolean;
  /**
   * On the automatic group only, and never saved: the inactive clients that
   * were left out of `clients`. Undefined when the tags could not be read, in
   * which case nobody was left out.
   */
  inactiveLeftOut?: string[];
  createdAt?: string;
  updatedAt?: string;
}

// Deployment Record
export interface DeploymentRecord {
  deploymentId: string;
  clientName: string;
  imageTag: string;
  imageDigest: string;
  status: 'pending' | 'running' | 'success' | 'failed';
  progress?: number; // 0-100 for running deployments
  initiatedBy: string;
  startTime: string;
  endTime?: string;
  logs?: string[];
  error?: string;
  // Mock indicator
  isMock?: boolean;
}

// Deployment Statistics
export interface DeploymentStats {
  totalDeployments: number;
  successfulDeployments: number;
  failedDeployments: number;
  averageDeploymentTime: number; // in minutes
  deploymentsToday: number;
  activeDeployments: number;
}

// User Session
export interface UserSession {
  email: string;
  name: string;
  role: 'admin' | 'customer-success' | 'viewer';
  isAuthenticated: boolean;
}
